import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores/auth.store';
import { useNotificationStore } from '../stores/notification.store';
import {
  getCurrentUser,
  updateUserProfile,
  updateUserInfo,
  changePassword,
  deleteUserAccount,
} from '../lib/profile.api';
import type { CompleteUser, UpdateProfileInput, UpdateUserInfoInput } from '../types/profile';
import MultiSelect from '../components/MultiSelect';
import {
  CORE_VALUES_OPTIONS,
  INTERESTS_OPTIONS,
  SKILLS_OPTIONS,
  LANGUAGES_OPTIONS,
} from '../constants/profile-options';

export default function Profile() {
  const navigate = useNavigate();
  const { user: authUser, logout } = useAuthStore();
  const { addNotification } = useNotificationStore();
  const [user, setUser] = useState<CompleteUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Form states
  const [activeTab, setActiveTab] = useState<'profile' | 'account' | 'security'>('profile');
  const [profileData, setProfileData] = useState<UpdateProfileInput>({});
  const [accountData, setAccountData] = useState<UpdateUserInfoInput>({});
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [deleteConfirm, setDeleteConfirm] = useState({
    password: '',
    confirmText: '',
  });

  // No longer needed - using multi-select directly

  useEffect(() => {
    loadUserData();
  }, []);

  const loadUserData = async () => {
    try {
      setLoading(true);
      const userData = await getCurrentUser();
      setUser(userData);

      // Initialize form data
      if (userData.profile) {
        setProfileData({
          age: userData.profile.age,
          dateOfBirth: userData.profile.dateOfBirth ? new Date(userData.profile.dateOfBirth).toISOString().split('T')[0] : undefined,
          city: userData.profile.city,
          country: userData.profile.country,
          currentProfession: userData.profile.currentProfession,
          currentCompany: userData.profile.currentCompany,
          yearsOfExperience: userData.profile.yearsOfExperience,
          dreamedProfession: userData.profile.dreamedProfession,
          dreamedCompany: userData.profile.dreamedCompany,
          coreValues: userData.profile.coreValues,
          interests: userData.profile.interests,
          skills: userData.profile.skills,
          languages: userData.profile.languages,
          shortTermGoals: userData.profile.shortTermGoals,
          longTermGoals: userData.profile.longTermGoals,
          lifeVision: userData.profile.lifeVision,
          personalityType: userData.profile.personalityType,
          workStyle: userData.profile.workStyle,
          availabilityHours: userData.profile.availabilityHours,
          bio: userData.profile.bio,
          linkedinUrl: userData.profile.linkedinUrl,
          websiteUrl: userData.profile.websiteUrl,
        });
      }

      setAccountData({
        name: userData.name,
        email: userData.email,
      });
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleProfileUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setSaving(true);
      setError('');
      setSuccess('');

      console.log('Updating profile with data:', profileData);

      // profileData already contains arrays from multi-select components
      await updateUserProfile(profileData);

      setSuccess('Profile updated successfully');
      addNotification({
        type: 'success',
        title: 'Profile Updated',
        message: 'Your profile has been updated successfully',
        duration: 4000,
      });

      await loadUserData();
    } catch (err: any) {
      console.error('Profile update error:', err);
      console.error('Error response:', err.response?.data);

      const errorMessage = err.response?.data?.error || err.message || 'Failed to update profile';
      setError(errorMessage);

      addNotification({
        type: 'error',
        title: 'Update Failed',
        message: errorMessage,
        duration: 6000,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleAccountUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setSaving(true);
      setError('');
      setSuccess('');

      await updateUserInfo(accountData);
      setSuccess('Account information updated successfully');
      await loadUserData();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update account');
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    if (passwordData.newPassword.length < 8) {
      setError('New password must be at least 8 characters long');
      return;
    }

    try {
      setSaving(true);
      setError('');
      setSuccess('');

      await changePassword({
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword,
      });

      setSuccess('Password changed successfully');
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to change password');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAccount = async (e: React.FormEvent) => {
    e.preventDefault();

    if (deleteConfirm.confirmText !== 'DELETE') {
      setError('Please type DELETE to confirm');
      return;
    }

    try {
      setSaving(true);
      setError('');

      await deleteUserAccount(deleteConfirm.password);
      logout();
      navigate('/login');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete account');
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-slate-900 text-xl">Loading profile...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 p-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/dashboard')}
            className="text-sm text-slate-600 hover:text-slate-900 mb-4 flex items-center transition-colors group"
          >
            <svg className="w-4 h-4 mr-2 group-hover:-translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Dashboard
          </button>
          <h1 className="text-4xl font-bold mb-2 text-slate-900">
            User Profile
          </h1>
          <p className="text-slate-600">Manage your personal information and settings</p>
        </div>

        {/* Tabs */}
        <div className="flex space-x-4 mb-8 border-b border-slate-200">
          <button
            onClick={() => setActiveTab('profile')}
            className={`pb-4 px-6 font-semibold transition-all ${
              activeTab === 'profile'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-slate-500 hover:text-slate-900'
            }`}
          >
            Profile
          </button>
          <button
            onClick={() => setActiveTab('account')}
            className={`pb-4 px-6 font-semibold transition-all ${
              activeTab === 'account'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-slate-500 hover:text-slate-900'
            }`}
          >
            Account
          </button>
          <button
            onClick={() => setActiveTab('security')}
            className={`pb-4 px-6 font-semibold transition-all ${
              activeTab === 'security'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-slate-500 hover:text-slate-900'
            }`}
          >
            Security
          </button>
        </div>

        {/* Messages */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-600">
            {error}
          </div>
        )}
        {success && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg text-green-600">
            {success}
          </div>
        )}

        {/* Profile Tab */}
        {activeTab === 'profile' && (
          <form onSubmit={handleProfileUpdate} className="space-y-8">
            {/* Personal Information */}
            <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6">
              <h2 className="text-2xl font-bold mb-6 text-slate-900">Personal Information</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium mb-2 text-slate-700">Age</label>
                  <input
                    type="number"
                    value={profileData.age || ''}
                    onChange={(e) => setProfileData({ ...profileData, age: parseInt(e.target.value) || undefined })}
                    className="w-full bg-white border border-slate-300 rounded-lg px-4 py-2 text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2 text-slate-700">Date of Birth</label>
                  <input
                    type="date"
                    value={profileData.dateOfBirth || ''}
                    onChange={(e) => setProfileData({ ...profileData, dateOfBirth: e.target.value })}
                    className="w-full bg-white border border-slate-300 rounded-lg px-4 py-2 text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2 text-slate-700">City</label>
                  <input
                    type="text"
                    value={profileData.city || ''}
                    onChange={(e) => setProfileData({ ...profileData, city: e.target.value })}
                    className="w-full bg-white border border-slate-300 rounded-lg px-4 py-2 text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2 text-slate-700">Country</label>
                  <input
                    type="text"
                    value={profileData.country || ''}
                    onChange={(e) => setProfileData({ ...profileData, country: e.target.value })}
                    className="w-full bg-white border border-slate-300 rounded-lg px-4 py-2 text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
            </div>

            {/* Professional Information */}
            <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6">
              <h2 className="text-2xl font-bold mb-6 text-slate-900">Professional Information</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium mb-2 text-slate-700">Current Profession</label>
                  <input
                    type="text"
                    value={profileData.currentProfession || ''}
                    onChange={(e) => setProfileData({ ...profileData, currentProfession: e.target.value })}
                    className="w-full bg-white border border-slate-300 rounded-lg px-4 py-2 text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2 text-slate-700">Current Company</label>
                  <input
                    type="text"
                    value={profileData.currentCompany || ''}
                    onChange={(e) => setProfileData({ ...profileData, currentCompany: e.target.value })}
                    className="w-full bg-white border border-slate-300 rounded-lg px-4 py-2 text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2 text-slate-700">Years of Experience</label>
                  <input
                    type="number"
                    value={profileData.yearsOfExperience || ''}
                    onChange={(e) => setProfileData({ ...profileData, yearsOfExperience: parseInt(e.target.value) || undefined })}
                    className="w-full bg-white border border-slate-300 rounded-lg px-4 py-2 text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2 text-slate-700">Dreamed Profession</label>
                  <input
                    type="text"
                    value={profileData.dreamedProfession || ''}
                    onChange={(e) => setProfileData({ ...profileData, dreamedProfession: e.target.value })}
                    className="w-full bg-white border border-slate-300 rounded-lg px-4 py-2 text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="What would you love to do?"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2 text-slate-700">Dreamed Company</label>
                  <input
                    type="text"
                    value={profileData.dreamedCompany || ''}
                    onChange={(e) => setProfileData({ ...profileData, dreamedCompany: e.target.value })}
                    className="w-full bg-white border border-slate-300 rounded-lg px-4 py-2 text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Where would you love to work?"
                  />
                </div>
              </div>
            </div>

            {/* Core Values & Interests */}
            <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6">
              <h2 className="text-2xl font-bold mb-6 text-slate-900">Core Values & Interests</h2>
              <div className="space-y-6">
                <MultiSelect
                  label="Core Values"
                  options={CORE_VALUES_OPTIONS}
                  selected={profileData.coreValues || []}
                  onChange={(values) => setProfileData({ ...profileData, coreValues: values })}
                  placeholder="Select your core values..."
                />
                <MultiSelect
                  label="Interests"
                  options={INTERESTS_OPTIONS}
                  selected={profileData.interests || []}
                  onChange={(values) => setProfileData({ ...profileData, interests: values })}
                  placeholder="Select your interests..."
                />
                <MultiSelect
                  label="Skills"
                  options={SKILLS_OPTIONS}
                  selected={profileData.skills || []}
                  onChange={(values) => setProfileData({ ...profileData, skills: values })}
                  placeholder="Select your skills..."
                />
                <MultiSelect
                  label="Languages"
                  options={LANGUAGES_OPTIONS}
                  selected={profileData.languages || []}
                  onChange={(values) => setProfileData({ ...profileData, languages: values })}
                  placeholder="Select languages you speak..."
                />
              </div>
            </div>

            {/* Life Goals */}
            <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6">
              <h2 className="text-2xl font-bold mb-6 text-slate-900">Life Goals</h2>
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium mb-2 text-slate-700">Short-Term Goals</label>
                  <textarea
                    value={profileData.shortTermGoals || ''}
                    onChange={(e) => setProfileData({ ...profileData, shortTermGoals: e.target.value })}
                    className="w-full bg-white border border-slate-300 rounded-lg px-4 py-2 text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent h-24"
                    placeholder="What do you want to achieve in the next 6-12 months?"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2 text-slate-700">Long-Term Goals</label>
                  <textarea
                    value={profileData.longTermGoals || ''}
                    onChange={(e) => setProfileData({ ...profileData, longTermGoals: e.target.value })}
                    className="w-full bg-white border border-slate-300 rounded-lg px-4 py-2 text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent h-24"
                    placeholder="What are your 3-5 year goals?"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2 text-slate-700">Life Vision</label>
                  <textarea
                    value={profileData.lifeVision || ''}
                    onChange={(e) => setProfileData({ ...profileData, lifeVision: e.target.value })}
                    className="w-full bg-white border border-slate-300 rounded-lg px-4 py-2 text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent h-24"
                    placeholder="Describe your ideal life and what success means to you"
                  />
                </div>
              </div>
            </div>

            {/* Personality & Work Style */}
            <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6">
              <h2 className="text-2xl font-bold mb-6 text-slate-900">Personality & Work Style</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium mb-2 text-slate-700">Personality Type</label>
                  <input
                    type="text"
                    value={profileData.personalityType || ''}
                    onChange={(e) => setProfileData({ ...profileData, personalityType: e.target.value })}
                    className="w-full bg-white border border-slate-300 rounded-lg px-4 py-2 text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="MBTI, Enneagram, etc."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2 text-slate-700">Work Style</label>
                  <select
                    value={profileData.workStyle || ''}
                    onChange={(e) => setProfileData({ ...profileData, workStyle: e.target.value })}
                    className="w-full bg-white border border-slate-300 rounded-lg px-4 py-2 text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Select...</option>
                    <option value="remote">Remote</option>
                    <option value="hybrid">Hybrid</option>
                    <option value="onsite">On-site</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2 text-slate-700">Availability (hours/week)</label>
                  <input
                    type="number"
                    value={profileData.availabilityHours || ''}
                    onChange={(e) => setProfileData({ ...profileData, availabilityHours: parseInt(e.target.value) || undefined })}
                    className="w-full bg-white border border-slate-300 rounded-lg px-4 py-2 text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
            </div>

            {/* Bio & Links */}
            <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6">
              <h2 className="text-2xl font-bold mb-6 text-slate-900">Bio & Links</h2>
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium mb-2 text-slate-700">Bio</label>
                  <textarea
                    value={profileData.bio || ''}
                    onChange={(e) => setProfileData({ ...profileData, bio: e.target.value })}
                    className="w-full bg-white border border-slate-300 rounded-lg px-4 py-2 text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent h-32"
                    placeholder="Tell us about yourself..."
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium mb-2 text-slate-700">LinkedIn URL</label>
                    <input
                      type="url"
                      value={profileData.linkedinUrl || ''}
                      onChange={(e) => setProfileData({ ...profileData, linkedinUrl: e.target.value })}
                      className="w-full bg-white border border-slate-300 rounded-lg px-4 py-2 text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="https://linkedin.com/in/..."
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2 text-slate-700">Website URL</label>
                    <input
                      type="url"
                      value={profileData.websiteUrl || ''}
                      onChange={(e) => setProfileData({ ...profileData, websiteUrl: e.target.value })}
                      className="w-full bg-white border border-slate-300 rounded-lg px-4 py-2 text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="https://..."
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <div className="flex justify-end">
              <button
                type="submit"
                disabled={saving}
                className="bg-blue-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? 'Saving...' : 'Save Profile'}
              </button>
            </div>
          </form>
        )}

        {/* Account Tab */}
        {activeTab === 'account' && (
          <form onSubmit={handleAccountUpdate} className="space-y-8">
            <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6">
              <h2 className="text-2xl font-bold mb-6 text-slate-900">Account Information</h2>
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium mb-2 text-slate-700">Name</label>
                  <input
                    type="text"
                    value={accountData.name || ''}
                    onChange={(e) => setAccountData({ ...accountData, name: e.target.value })}
                    className="w-full bg-white border border-slate-300 rounded-lg px-4 py-2 text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2 text-slate-700">Email</label>
                  <input
                    type="email"
                    value={accountData.email || ''}
                    onChange={(e) => setAccountData({ ...accountData, email: e.target.value })}
                    className="w-full bg-white border border-slate-300 rounded-lg px-4 py-2 text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end">
              <button
                type="submit"
                disabled={saving}
                className="bg-blue-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? 'Saving...' : 'Update Account'}
              </button>
            </div>
          </form>
        )}

        {/* Security Tab */}
        {activeTab === 'security' && (
          <div className="space-y-8">
            {/* Change Password */}
            <form onSubmit={handlePasswordChange} className="bg-white border border-slate-200 rounded-xl shadow-sm p-6">
              <h2 className="text-2xl font-bold mb-6 text-slate-900">Change Password</h2>
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium mb-2 text-slate-700">Current Password</label>
                  <input
                    type="password"
                    value={passwordData.currentPassword}
                    onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                    className="w-full bg-white border border-slate-300 rounded-lg px-4 py-2 text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2 text-slate-700">New Password</label>
                  <input
                    type="password"
                    value={passwordData.newPassword}
                    onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                    className="w-full bg-white border border-slate-300 rounded-lg px-4 py-2 text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                    minLength={8}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2 text-slate-700">Confirm New Password</label>
                  <input
                    type="password"
                    value={passwordData.confirmPassword}
                    onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                    className="w-full bg-white border border-slate-300 rounded-lg px-4 py-2 text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                    minLength={8}
                  />
                </div>
              </div>
              <div className="mt-6 flex justify-end">
                <button
                  type="submit"
                  disabled={saving}
                  className="bg-blue-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving ? 'Changing...' : 'Change Password'}
                </button>
              </div>
            </form>

            {/* Delete Account */}
            <form onSubmit={handleDeleteAccount} className="bg-white border border-red-300 rounded-xl shadow-sm p-6">
              <h2 className="text-2xl font-bold mb-6 text-red-600">Danger Zone</h2>
              <p className="text-slate-600 mb-6">
                Once you delete your account, there is no going back. Please be certain.
              </p>
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium mb-2 text-slate-700">Enter your password</label>
                  <input
                    type="password"
                    value={deleteConfirm.password}
                    onChange={(e) => setDeleteConfirm({ ...deleteConfirm, password: e.target.value })}
                    className="w-full bg-white border border-red-300 rounded-lg px-4 py-2 text-slate-900 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2 text-slate-700">
                    Type "DELETE" to confirm
                  </label>
                  <input
                    type="text"
                    value={deleteConfirm.confirmText}
                    onChange={(e) => setDeleteConfirm({ ...deleteConfirm, confirmText: e.target.value })}
                    className="w-full bg-white border border-red-300 rounded-lg px-4 py-2 text-slate-900 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    required
                  />
                </div>
              </div>
              <div className="mt-6 flex justify-end">
                <button
                  type="submit"
                  disabled={saving}
                  className="bg-red-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-red-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving ? 'Deleting...' : 'Delete Account'}
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}
